import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Trophy,
  Award,
  Star,
  Medal,
  Crown,
  Zap,
  Target,
  Calendar,
  Download,
  Share2,
  Search,
  CheckCircle,
  Lock,
  Flame,
  Brain,
  BookOpen,
  TrendingUp,
  Shield,
  Heart,
  Coffee,
  Sparkles,
  Users,
  Clock,
  Rocket,
  Gift,
  X,
  Layers,
  Gem,
  Badge as BadgeIcon,
  RefreshCw,
} from 'lucide-react';
import {
  Leaderboardservice,
  BadgeMetricsResponse,
  UserBadge,
  LockedBadge,
  UserCertificatesResponse,
  CertificationCertificate,
  CourseCertificate,
  LearningPathCertificate,
} from '@/services/leaderboard.service';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { downloadFile } from '@/utils/downloadFile';
import { businessService } from '@/services/business.service';
import { certificateRegenerationService, type CertificateType } from '@/services/certificate-regeneration.service';
import toast from 'react-hot-toast';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_GAMIFICATION_PERMISSION } from '@/constants/constant-permissions';
import SocialShare from '@/components/ui/SocialShare';

// Unified Certificate type for display (normalized from all 3 certificate types)
interface UnifiedCertificate {
  _id: string;
  type: 'course' | 'certification' | 'learning_path';
  title: string;
  description: string;
  certificate_number: string;
  verification_url: string;
  issued_date: string;
  holder_info: {
    user_id: string;
    user_name: string;
    user_email: string;
  };
  pdf_url: string;
  status: string;
  banner_image_url?: string;
  // Certification specific
  tier?: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM';
  percentage_achieved?: number;
  score_achieved?: number;
  // Course specific
  time_spent_seconds?: number;
  // Learning path specific
  total_courses?: number;
  completed_courses?: number;
  // Original data for detail modal
  originalData: CertificationCertificate | CourseCertificate | LearningPathCertificate;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  iconType?: string;
  iconColor?: string;
  color: string;
  badgeUrl?: string;
  earnedDate?: string;
  requirement: string;
  progress?: number;
  maxProgress?: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  points: number;
  category: string;
  locked: boolean;
}

interface UserStats {
  totalCertificates: number;
  totalBadges: number;
  totalPoints: number;
  currentLevel: number;
  nextLevelPoints: number;
  rank: number;
  totalUsers: number;
  completionRate: number;
  monthlyGrowth: number;
}

// Helper function to get icon component based on icon_type
const getIconComponent = (iconType: string): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    'crown': <Crown className="w-8 h-8" />,
    'flame': <Flame className="w-8 h-8" />,
    'lightning': <Zap className="w-8 h-8" />,
    'zap': <Zap className="w-8 h-8" />,
    'brain': <Brain className="w-8 h-8" />,
    'target': <Target className="w-8 h-8" />,
    'coffee': <Coffee className="w-8 h-8" />,
    'heart': <Heart className="w-8 h-8" />,
    'trending-up': <TrendingUp className="w-8 h-8" />,
    'award': <Award className="w-8 h-8" />,
    'gem': <Gem className="w-8 h-8" />,
    'sparkles': <Sparkles className="w-8 h-8" />,
    'rocket': <Rocket className="w-8 h-8" />,
    'star': <Star className="w-8 h-8" />,
    'medal': <Medal className="w-8 h-8" />,
    'trophy': <Trophy className="w-8 h-8" />,
    'shield': <Shield className="w-8 h-8" />,
    'book-open': <BookOpen className="w-8 h-8" />,
    'users': <Users className="w-8 h-8" />,
    'gift': <Gift className="w-8 h-8" />,
  };
  return iconMap[iconType?.toLowerCase()] || <BadgeIcon className="w-8 h-8" />;
};

// Helper function to get solid background color based on icon_color
const getIconBgColor = (iconColor?: string, rarity?: string): string => {
  if (iconColor) {
    // If it's already a hex color or rgb, return as-is for inline style
    if (iconColor.startsWith('#') || iconColor.startsWith('rgb')) {
      return iconColor;
    }
    // Map color names to Tailwind classes
    const colorMap: Record<string, string> = {
      'yellow': 'bg-yellow-500',
      'gold': 'bg-amber-500',
      'orange': 'bg-orange-500',
      'red': 'bg-red-500',
      'blue': 'bg-blue-500',
      'cyan': 'bg-cyan-500',
      'purple': 'bg-purple-500',
      'violet': 'bg-violet-500',
      'green': 'bg-green-500',
      'emerald': 'bg-emerald-500',
      'pink': 'bg-pink-500',
      'indigo': 'bg-indigo-500',
      'amber': 'bg-amber-500',
      'teal': 'bg-teal-500',
      'rose': 'bg-rose-500',
      'gray': 'bg-gray-500',
      'slate': 'bg-slate-500',
    };
    return colorMap[iconColor.toLowerCase()] || 'bg-indigo-500';
  }

  // Fallback to rarity-based colors
  const rarityColors: Record<string, string> = {
    'common': 'bg-gray-500',
    'rare': 'bg-blue-500',
    'epic': 'bg-purple-500',
    'legendary': 'bg-amber-500',
  };
  return rarityColors[rarity || 'common'] || 'bg-gray-500';
};

// Check if color is a hex/rgb value for inline style
const isInlineColor = (color?: string): boolean => {
  if (!color) return false;
  return color.startsWith('#') || color.startsWith('rgb');
};

// Helper functions for certificate tier styling
const getTierConfig = (tier: string) => {
  const configs: Record<string, { gradient: string; bgLight: string; text: string; border: string; icon: string }> = {
    'BRONZE': {
      gradient: 'from-amber-600 via-orange-500 to-amber-700',
      bgLight: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-300',
      icon: '🥉'
    },
    'SILVER': {
      gradient: 'from-slate-400 via-gray-300 to-slate-500',
      bgLight: 'bg-slate-50',
      text: 'text-slate-700',
      border: 'border-slate-300',
      icon: '🥈'
    },
    'GOLD': {
      gradient: 'from-yellow-400 via-amber-300 to-yellow-500',
      bgLight: 'bg-yellow-50',
      text: 'text-yellow-700',
      border: 'border-yellow-400',
      icon: '🥇'
    },
    'PLATINUM': {
      gradient: 'from-cyan-400 via-teal-300 to-cyan-500',
      bgLight: 'bg-cyan-50',
      text: 'text-cyan-700',
      border: 'border-cyan-400',
      icon: '💎'
    }
  };
  return configs[tier] || configs['BRONZE'];
};

const getScoreColor = (percentage: number) => {
  if (percentage >= 90) return 'text-green-600 bg-green-50 border-green-200';
  if (percentage >= 70) return 'text-blue-600 bg-blue-50 border-blue-200';
  if (percentage >= 50) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

// Helper function to format seconds to HH:MM:SS
const formatTimeSpent = (seconds?: number | null): string => {
  if (seconds === undefined || seconds === null) {
    return '00:00:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const getVerificationUrl = (_verificationUrl: string | undefined, certificateNumber: string): string => {
  return `${window.location.origin}/certificate/verify/${certificateNumber}`;
};

// Skeleton Components
const StatsSkeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-white/20 rounded animate-pulse">
            <div className="w-4 h-4" />
          </div>
          <div className="space-y-1">
            <div className="h-6 w-12 bg-white/30 rounded animate-pulse" />
            <div className="h-3 w-16 bg-white/20 rounded animate-pulse" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const BadgeCardSkeleton = ({ count = 6 }: { count?: number }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 flex items-center gap-3 sm:block sm:text-center">
        <div className="w-14 h-14 flex-shrink-0 sm:mx-auto sm:mb-3 rounded-full bg-gray-200 animate-pulse" />
        <div className="h-4 w-20 mx-auto bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-5 w-12 mx-auto bg-gray-100 rounded-full animate-pulse" />
      </div>
    ))}
  </div>
);

// Empty State Component
const EmptyState = ({
  icon: Icon,
  title,
  description,
  actionText
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  actionText?: string;
}) => (
  <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-indigo-50/30 rounded-xl border border-dashed border-gray-300">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
      <Icon className="w-8 h-8 text-indigo-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">{description}</p>
    {actionText && (
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
        <Rocket className="w-4 h-4" />
        {actionText}
      </div>
    )}
  </div>
);

export default function GamificationBadges() {
  const { LEARNER_GAMIFICATION_READ, LEARNER_GAMIFICATION_WRITE } = LEARNER_GAMIFICATION_PERMISSION;
  usePermissionRouteAccess([LEARNER_GAMIFICATION_READ, LEARNER_GAMIFICATION_WRITE]);
  const canWrite = useHasPermission([LEARNER_GAMIFICATION_WRITE]);

  // Tab and search state
  const [activeTab, setActiveTab] = useState<'all' | 'certificates' | 'badges'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory] = useState('all');

  // Badge states
  const [showBadgeDetail, setShowBadgeDetail] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

  // Certificate states
  const [certificatesData, setCertificatesData] = useState<UserCertificatesResponse | null>(null);
  const [showCertificateDetail, setShowCertificateDetail] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<UnifiedCertificate | null>(null);
  const [showCertificateShare, setShowCertificateShare] = useState(false);
  const [shareCertificate, setShareCertificate] = useState<UnifiedCertificate | null>(null);

  // API data states
  const [badgeMetrics, setBadgeMetrics] = useState<BadgeMetricsResponse | null>(null);
  const [earnedBadgesData, setEarnedBadgesData] = useState<UserBadge[]>([]);
  const [lockedBadgesData, setLockedBadgesData] = useState<LockedBadge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Social sharing state
  const [linkedinOrgId, setLinkedinOrgId] = useState<string | undefined>(undefined);

  // Certificate regeneration states
  const [allowRegeneration, setAllowRegeneration] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regenerateCertId, setRegenerateCertId] = useState<string | null>(null);
  const [regenerateCertType, setRegenerateCertType] = useState<CertificateType | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY);

  // Fetch badge data from APIs
  useEffect(() => {
    const fetchBadgeData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [metricsResponse, earnedResponse, lockedResponse, certificatesResponse, settingsResponse] = await Promise.all([
          Leaderboardservice.getBadgeMetrics(),
          Leaderboardservice.listUserBadges(),
          Leaderboardservice.getLockedBadges(),
          Leaderboardservice.getUserCertificates(),
          businessService.getBusinessSettings().catch(() => null)
        ]);
        setBadgeMetrics(metricsResponse);
        setEarnedBadgesData(Array.isArray(earnedResponse) ? earnedResponse : []);
        setLockedBadgesData(Array.isArray(lockedResponse) ? lockedResponse : []);
        setCertificatesData(certificatesResponse);
        if (settingsResponse?.data?.allow_learner_certificate_regeneration) {
          setAllowRegeneration(true);
        }
        if (settingsResponse?.data?.linkedin_organization_id) {
          setLinkedinOrgId(settingsResponse.data.linkedin_organization_id);
        }
      } catch (err) {
        console.error('Failed to fetch badge data:', err);
        setError('Failed to load badge data');
        // Set empty arrays on error to prevent map errors
        setEarnedBadgesData([]);
        setLockedBadgesData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBadgeData();
  }, []);

  // Refetch badges when debounced search term changes
  useEffect(() => {
    const searchBadges = async () => {
      if (!isLoading) {
        try {
          const earnedResponse = await Leaderboardservice.listUserBadges(debouncedSearchTerm || undefined);
          setEarnedBadgesData(Array.isArray(earnedResponse) ? earnedResponse : []);
        } catch (err) {
          console.error('Failed to search badges:', err);
        }
      }
    };

    searchBadges();
  }, [debouncedSearchTerm]);

  // Transform API data to Badge format
  const earnedBadges: Badge[] = useMemo(() => {
    // Handle case where API returns array directly or wrapped in an object
    const badgesArray = Array.isArray(earnedBadgesData) ? earnedBadgesData : [];
    return badgesArray.map((badge) => ({
      id: badge.badge_id,
      name: badge.badge_name,
      description: badge.description,
      icon: getIconComponent(badge.icon_type),
      iconType: badge.icon_type,
      iconColor: badge.icon_color,
      color: getIconBgColor(badge.icon_color, badge.rarity),
      badgeUrl: badge.badge_url,
      earnedDate: badge.earned_at,
      requirement: `Requires ${badge.xp_required} XP`,
      rarity: badge.rarity,
      points: badge.points || badge.xp_required,
      category: badge.category,
      locked: false
    }));
  }, [earnedBadgesData]);

  const lockedBadges: Badge[] = useMemo(() => {
    // Handle case where API returns array directly or wrapped in an object
    const badgesArray = Array.isArray(lockedBadgesData) ? lockedBadgesData : [];
    return badgesArray.map((badge) => ({
      id: badge.badge_id,
      name: badge.badge_name,
      description: badge.description,
      icon: getIconComponent(badge.icon_type),
      iconType: badge.icon_type,
      iconColor: badge.icon_color,
      color: 'bg-gray-400',
      badgeUrl: badge.badge_url,
      requirement: `Requires ${badge.xp_required} XP`,
      progress: badge.user_xp || 0,
      maxProgress: badge.xp_required,
      rarity: badge.rarity,
      points: badge.points || badge.xp_required,
      category: badge.category,
      locked: true
    }));
  }, [lockedBadgesData]);

  // Computed certificates - normalize all 3 types into unified format
  const activeCertificates = useMemo((): UnifiedCertificate[] => {
    if (!certificatesData) return [];

    const unified: UnifiedCertificate[] = [];

    // Add certification certificates
    (certificatesData.certification_certificates || []).forEach(cert => {
      unified.push({
        _id: cert._id,
        type: 'certification',
        title: cert.certification_title,
        description: cert.metadata.certification_description,
        certificate_number: cert.certificate_number,
        verification_url: cert.verification_url,
        issued_date: cert.issued_date,
        holder_info: cert.holder_info,
        pdf_url: cert.pdf_url,
        status: cert.status,
        banner_image_url: cert.banner_image_url,
        tier: cert.tier,
        percentage_achieved: cert.metadata.percentage_achieved,
        score_achieved: cert.metadata.score_achieved,
        originalData: cert
      });
    });

    // Add course certificates
    (certificatesData.course_certificates || []).forEach(cert => {
      unified.push({
        _id: cert._id,
        type: 'course',
        title: cert.course_title,
        description: cert.metadata.course_description,
        certificate_number: cert.certificate_number,
        verification_url: cert.verification_url,
        issued_date: cert.issued_date,
        holder_info: cert.holder_info,
        pdf_url: cert.pdf_url,
        status: cert.status,
        banner_image_url: cert.banner_image_url,
        time_spent_seconds: cert.metadata.time_spent_seconds,
        originalData: cert
      });
    });

    // Add learning path certificates
    (certificatesData.learning_path_certificates || []).forEach(cert => {
      unified.push({
        _id: cert._id,
        type: 'learning_path',
        title: cert.learning_path_title,
        description: cert.metadata.learning_path_description,
        certificate_number: cert.certificate_number,
        verification_url: cert.verification_url,
        issued_date: cert.issued_date,
        holder_info: cert.holder_info,
        pdf_url: cert.pdf_url,
        status: cert.status,
        banner_image_url: cert.banner_image_url,
        total_courses: cert.metadata.total_courses,
        completed_courses: cert.metadata.completed_courses,
        percentage_achieved: cert.metadata.average_progress_percentage,
        originalData: cert
      });
    });

    // Sort by issued_date descending (newest first)
    return unified.sort((a, b) => new Date(b.issued_date).getTime() - new Date(a.issued_date).getTime());
  }, [certificatesData]);

  // User stats from API metrics
  const totalCertificatesCount = useMemo(() => {
    return (badgeMetrics?.course_certificates_count || 0) +
           (badgeMetrics?.certification_certificates_count || 0) +
           (badgeMetrics?.learning_path_certificates_count || 0);
  }, [badgeMetrics]);

  const userStats: UserStats = useMemo(() => ({
    totalCertificates: totalCertificatesCount,
    totalBadges: badgeMetrics?.total_badge_count || 0,
    totalPoints: badgeMetrics?.user_xp || 0,
    currentLevel: badgeMetrics?.level || 1,
    nextLevelPoints: badgeMetrics?.next_level_xp || 1000,
    rank: badgeMetrics?.rank || 0,
    totalUsers: 0,
    completionRate: 0,
    monthlyGrowth: 0
  }), [badgeMetrics, totalCertificatesCount]);

  // Handler for certificate click
  const handleCertificateClick = (cert: UnifiedCertificate) => {
    setSelectedCertificate(cert);
    setShowCertificateDetail(true);
  };

  const handleCertificateShare = (cert: UnifiedCertificate) => {
    setShareCertificate(cert);
    setShowCertificateShare(true);
  };

  const getRarityBadgeColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'bg-gray-100 text-gray-700';
      case 'rare': return 'bg-blue-100 text-blue-700';
      case 'epic': return 'bg-purple-100 text-purple-700';
      case 'legendary': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleBadgeClick = (badge: Badge) => {
    setSelectedBadge(badge);
    setShowBadgeDetail(true);
  };

  // Handler for certificate download
  const handleDownloadCertificate = async (pdfUrl: string, _certificateTitle: string, certificateNumber?: string) => {
    const toastId = toast.loading('Preparing download...');
    try {
      // Extract the key from the full S3 URL
      // Example: https://sgp1.digitaloceanspaces.com/skillzen-local/1b7f2a0e-9de3-457a-9f92-204fccd09013.pdf
      // Extract: 1b7f2a0e-9de3-457a-9f92-204fccd09013.pdf
      const urlParts = pdfUrl.split('/');
      const key = urlParts[urlParts.length - 1]; // Get the last part (filename with UUID)

      // Create filename - always use certificate number
      let filename: string;

      if (certificateNumber) {
        filename = `Certificate_${certificateNumber}.pdf`;
      } else {
        filename = `Certificate_${Date.now()}.pdf`;
      }

      await downloadFile(key, filename);
      toast.success('Certificate downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download certificate', { id: toastId });
    }
  };

  // Open regenerate confirmation for a certificate
  const openRegenerateConfirm = useCallback((certId: string, certType: CertificateType) => {
    setRegenerateCertId(certId);
    setRegenerateCertType(certType);
    setShowRegenerateConfirm(true);
  }, []);

  // Handle learner certificate regeneration
  const handleRegenerateCertificate = async () => {
    if (!regenerateCertId || !regenerateCertType) return;

    try {
      setRegenerating(true);
      await certificateRegenerationService.learnerRegenerate(regenerateCertId, regenerateCertType);

      toast.success('Certificate regenerated successfully!');
      setShowRegenerateConfirm(false);

      // Refresh certificates data
      const certificatesResponse = await Leaderboardservice.getUserCertificates();
      setCertificatesData(certificatesResponse);
    } catch (err: any) {
      const errorData = err?.response?.data;
      const errorCode = errorData?.error?.error;
      const message = errorData?.error?.error_description || errorData?.error_description || errorData?.message || err?.message || 'Failed to regenerate certificate';

      if (errorCode === 'NO_CHANGES_DETECTED') {
        toast.error(message);
        setShowRegenerateConfirm(false);
        return;
      }

      toast.error(message);
    } finally {
      setRegenerating(false);
    }
  };

  // Filter certificates (using debounced search for consistency with badges)
  const filteredCertificates = useMemo(() => {
    if (!debouncedSearchTerm) return activeCertificates;
    const search = debouncedSearchTerm.toLowerCase();
    return activeCertificates.filter(cert =>
      cert.title.toLowerCase().includes(search) ||
      cert.description.toLowerCase().includes(search) ||
      cert.certificate_number.toLowerCase().includes(search) ||
      cert.holder_info.user_name.toLowerCase().includes(search) ||
      cert.type.toLowerCase().includes(search) ||
      (cert.tier && cert.tier.toLowerCase().includes(search))
    );
  }, [activeCertificates, debouncedSearchTerm]);

  const filteredEarnedBadges = useMemo(() =>
    earnedBadges.filter(badge =>
      (filterCategory === 'all' || badge.category === filterCategory)
    ), [earnedBadges, filterCategory]
  );

  const filteredLockedBadges = useMemo(() =>
    lockedBadges.filter(badge =>
      (filterCategory === 'all' || badge.category === filterCategory)
    ), [lockedBadges, filterCategory]
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Compact Hero Header */}
      <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-xl p-4 sm:p-6 text-white overflow-hidden">
        {/* Animated background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10">
          {/* Header Row */}
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <Trophy className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold leading-tight">Achievements & Recognition</h1>
                <p className="text-indigo-100 text-sm">Track your learning journey</p>
              </div>
            </div>
          </div>

          {/* Compact Stats Grid - 4 Cards */}
          {isLoading ? (
            <StatsSkeleton />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {/* Certifications (Total) */}
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-amber-400/30 rounded">
                    <Award className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{totalCertificatesCount}</p>
                    <p className="text-xs text-indigo-100">Certifications</p>
                  </div>
                </div>
              </div>
              {/* Badges */}
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-400/30 rounded">
                    <Medal className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{userStats.totalBadges}</p>
                    <p className="text-xs text-indigo-100">Badges</p>
                  </div>
                </div>
              </div>
              {/* XP Points */}
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-yellow-400/30 rounded">
                    <Star className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{userStats.totalPoints.toLocaleString()}</p>
                    <p className="text-xs text-indigo-100">XP Points</p>
                  </div>
                </div>
              </div>
              {/* Rank */}
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-rose-400/30 rounded">
                    <Crown className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">#{userStats.rank}</p>
                    <p className="text-xs text-indigo-100">Rank</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          {/* On phones the three tabs share the width ("All" short label);
              overflow-x-auto is a safety net for very narrow screens. */}
          <nav className="flex overflow-x-auto">
            {[
              { key: 'all', label: 'All Achievements', shortLabel: 'All', icon: Trophy },
              { key: 'certificates', label: 'Certificates', icon: Award, count: activeCertificates.length },
              { key: 'badges', label: 'Badges', icon: BadgeIcon, count: earnedBadges.length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex-1 sm:flex-none flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                {tab.shortLabel ? (
                  <>
                    <span className="sm:hidden">{tab.shortLabel}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </>
                ) : (
                  tab.label
                )}
                {tab.count !== undefined && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
        <div className="p-3 sm:p-6 border-b border-gray-200 bg-gray-50/50">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search achievements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-6">
          {/* Loading State */}
          {isLoading ? (
            <div className="space-y-8">
              {/* Certificates Skeleton */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
                    <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="h-4 w-16 bg-gray-100 rounded animate-pulse" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="h-32 bg-gray-200 rounded-lg animate-pulse mb-3" />
                      <div className="h-5 w-3/4 bg-gray-200 rounded animate-pulse mb-2" />
                      <div className="h-4 w-1/2 bg-gray-100 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Badges Skeleton */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
                    <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="h-4 w-16 bg-gray-100 rounded animate-pulse" />
                </div>
                <BadgeCardSkeleton count={6} />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* All Achievements Tab */}
              {activeTab === 'all' && (
                <div className="space-y-8">
                  {/* Certificates Preview */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Award className="w-5 h-5 text-indigo-600" />
                        <h3 className="font-semibold text-gray-900">
                          Certificates ({filteredCertificates.length})
                          {debouncedSearchTerm && filteredCertificates.length !== activeCertificates.length}
                        </h3>
                      </div>
                      {filteredCertificates.length > 0 && (
                        <button
                          onClick={() => setActiveTab('certificates')}
                          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          View all →
                        </button>
                      )}
                    </div>
                    {filteredCertificates.length === 0 ? (
                      debouncedSearchTerm ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                          <Search className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No certificates found matching "{debouncedSearchTerm}"</p>
                        </div>
                      ) : (
                        <EmptyState
                          icon={Award}
                          title="Earn Your First Certificate"
                          description="Complete courses to earn professional certificates that showcase your skills and knowledge to employers."
                        />
                      )
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                        {filteredCertificates.slice(0, 3).map((cert) => {
                          const tierConfig = cert.tier ? getTierConfig(cert.tier) : null;
                          const typeLabel = cert.type === 'course' ? 'Course' : cert.type === 'learning_path' ? 'Learning Path' : 'Certification';
                          const typeGradient = cert.type === 'course' ? 'from-emerald-500 via-teal-500 to-cyan-600' : cert.type === 'learning_path' ? 'from-violet-500 via-purple-500 to-indigo-600' : tierConfig?.gradient || 'from-indigo-500 to-purple-600';
                          const typeIcon = cert.type === 'course' ? <BookOpen className="w-3.5 h-3.5" /> : cert.type === 'learning_path' ? <Layers className="w-3.5 h-3.5" /> : <Award className="w-3.5 h-3.5" />;
                          return (
                            <div
                              key={cert._id}
                              className="group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer"
                              onClick={() => handleCertificateClick(cert)}
                            >
                              {/* Banner with gradient overlay */}
                              <div className={`relative h-36 bg-gradient-to-br ${typeGradient} overflow-hidden`}>
                                {cert.banner_image_url ? (
                                  <>
                                    <img
                                      src={cert.banner_image_url}
                                      alt={cert.title}
                                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                  </>
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <Award className="w-16 h-16 text-white/20" />
                                  </div>
                                )}

                                {/* Type Badge */}
                                <div className="absolute top-3 left-3">
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/95 shadow-lg backdrop-blur-sm text-gray-700">
                                    {typeIcon}
                                    {typeLabel}
                                  </span>
                                </div>
                                {/* Certificate Number */}
                                <div className="absolute bottom-3 left-3 right-3">
                                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-black/40 backdrop-blur-sm text-white/90 text-xs font-mono max-w-full truncate">
                                    <Shield className="w-3 h-3" />
                                    {cert.certificate_number}
                                  </span>
                                </div>
                              </div>

                              {/* Content */}
                              <div className="p-4 sm:p-5">
                                <h4 className="font-bold text-gray-900 line-clamp-2 mb-3 group-hover:text-indigo-600 transition-colors leading-snug">
                                  {cert.title}
                                </h4>

                                <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                                  {cert.description}
                                </p>

                                {/* Stats Row - varies by type */}
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-4">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    {new Date(cert.issued_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                  {cert.type === 'certification' && cert.score_achieved !== undefined && (
                                    <span className="flex items-center gap-1">
                                      <Target className="w-3.5 h-3.5" />
                                      Score: {cert.score_achieved}
                                    </span>
                                  )}
                                  {cert.type === 'certification' && cert.tier && tierConfig && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${tierConfig.bgLight} ${tierConfig.text} ${tierConfig.border} border`}>
                                      <span>{tierConfig.icon}</span>
                                      {cert.tier}
                                    </span>
                                  )}
                                  {cert.type === 'course' && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3.5 h-3.5" />
                                      {formatTimeSpent(cert.time_spent_seconds)}
                                    </span>
                                  )}
                                  {cert.type === 'learning_path' && cert.completed_courses !== undefined && (
                                    <span className="flex items-center gap-1">
                                      <BookOpen className="w-3.5 h-3.5" />
                                      {cert.completed_courses}/{cert.total_courses} courses
                                    </span>
                                  )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (canWrite) handleDownloadCertificate(cert.pdf_url, cert.title, cert.certificate_number);
                                    }}
                                    disabled={!canWrite}
                                    title={!canWrite ? 'You do not have permission to download' : undefined}
                                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg transition-colors ${
                                      !canWrite ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'
                                    }`}
                                  >
                                    <Download className="w-4 h-4" />
                                    Download
                                  </button>
                                  {allowRegeneration && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openRegenerateConfirm(cert._id, cert.type as CertificateType);
                                      }}
                                      className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                      title="Regenerate Certificate"
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCertificateShare(cert);
                                    }}
                                    className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                  >
                                    <Share2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Badges Preview */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <BadgeIcon className="w-5 h-5 text-purple-600" />
                        <h3 className="font-semibold text-gray-900">Badges ({earnedBadges.length})</h3>
                      </div>
                      <button
                        onClick={() => setActiveTab('badges')}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        View all →
                      </button>
                    </div>
                    {earnedBadges.length === 0 ? (
                      <EmptyState
                        icon={BadgeIcon}
                        title="Unlock Your First Badge"
                        description="Badges are rewards for your learning achievements. Complete lessons, quizzes, and courses to start collecting badges!"
                        actionText="Explore Courses"
                      />
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2.5 sm:gap-4">
                        {earnedBadges.slice(0, 6).map((badge) => (
                          <div
                            key={badge.id}
                            onClick={() => handleBadgeClick(badge)}
                            /* Phone: one-column list row (icon left, details right).
                               sm and up: the original stacked, centred card. */
                            className="group bg-white border border-gray-200 rounded-xl p-3 sm:p-4 hover:shadow-lg transition-all duration-300 cursor-pointer flex items-center gap-3 text-left sm:block sm:text-center"
                          >
                            <div
                              className={`w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 sm:mx-auto sm:mb-3 rounded-full ${!badge.badgeUrl ? (isInlineColor(badge.iconColor) ? '' : badge.color) : ''} flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300 shadow-lg overflow-hidden`}
                              style={!badge.badgeUrl && isInlineColor(badge.iconColor) ? { backgroundColor: badge.iconColor } : undefined}
                            >
                              {badge.badgeUrl ? (
                                <img src={badge.badgeUrl} alt={badge.name} className="w-full h-full object-cover" />
                              ) : (
                                badge.icon
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-sm text-gray-900 mb-0.5 sm:mb-1 line-clamp-1">{badge.name}</h3>
                              <p className="text-xs text-gray-600 mb-1.5 sm:mb-2 line-clamp-2">{badge.description}</p>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:block">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getRarityBadgeColor(badge.rarity)}`}>
                                  {badge.rarity}
                                </span>
                                <p className="text-xs text-gray-500 sm:mt-2">{badge.points} pts</p>
                                {badge.earnedDate && (
                                  <p className="text-xs text-gray-400 sm:mt-1">
                                    <span className="sm:hidden">· </span>
                                    {new Date(badge.earnedDate).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Certificates Tab */}
              {activeTab === 'certificates' && (
                <div className="space-y-4 sm:space-y-6">
                  {filteredCertificates.length === 0 ? (
                    searchTerm ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <Search className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No certificates found matching "{searchTerm}"</p>
                      </div>
                    ) : (
                      <EmptyState
                        icon={Award}
                        title="Your Certificates Await"
                        description="Complete courses to earn professional certificates. Each certificate validates your expertise and can be shared with employers on LinkedIn and other platforms."
                      />
                    )
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                      {filteredCertificates.map((cert) => {
                        const tierConfig = cert.tier ? getTierConfig(cert.tier) : null;
                        const typeLabel = cert.type === 'course' ? 'Course' : cert.type === 'learning_path' ? 'Learning Path' : 'Certification';
                        const typeGradient = cert.type === 'course' ? 'from-emerald-500 via-teal-500 to-cyan-600' : cert.type === 'learning_path' ? 'from-violet-500 via-purple-500 to-indigo-600' : tierConfig?.gradient || 'from-indigo-500 to-purple-600';
                        const typeIcon = cert.type === 'course' ? <BookOpen className="w-3.5 h-3.5" /> : cert.type === 'learning_path' ? <Layers className="w-3.5 h-3.5" /> : <Award className="w-3.5 h-3.5" />;
                        return (
                          <div
                            key={cert._id}
                            className="group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer"
                            onClick={() => handleCertificateClick(cert)}
                          >
                            {/* Banner with gradient overlay */}
                            <div className={`relative h-36 bg-gradient-to-br ${typeGradient} overflow-hidden`}>
                              {cert.banner_image_url ? (
                                <>
                                  <img
                                    src={cert.banner_image_url}
                                    alt={cert.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                </>
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Award className="w-16 h-16 text-white/20" />
                                </div>
                              )}

                              {/* Type Badge */}
                              <div className="absolute top-3 left-3">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/95 shadow-lg backdrop-blur-sm text-gray-700">
                                  {typeIcon}
                                  {typeLabel}
                                </span>
                              </div>
                              {/* Certificate Number */}
                              <div className="absolute bottom-3 left-3 right-3">
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-black/40 backdrop-blur-sm text-white/90 text-xs font-mono max-w-full truncate">
                                  <Shield className="w-3 h-3" />
                                  {cert.certificate_number}
                                </span>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="p-4 sm:p-5">
                              <h4 className="font-bold text-gray-900 line-clamp-2 mb-3 group-hover:text-indigo-600 transition-colors leading-snug">
                                {cert.title}
                              </h4>

                              <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                                {cert.description}
                              </p>

                              {/* Stats Row - varies by type */}
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-4">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  {new Date(cert.issued_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                                {cert.type === 'certification' && cert.score_achieved !== undefined && (
                                  <span className="flex items-center gap-1">
                                    <Target className="w-3.5 h-3.5" />
                                    Score: {cert.score_achieved}
                                  </span>
                                )}
                                {cert.type === 'certification' && cert.tier && tierConfig && (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${tierConfig.bgLight} ${tierConfig.text} ${tierConfig.border} border`}>
                                    <span>{tierConfig.icon}</span>
                                    {cert.tier}
                                  </span>
                                )}
                                {cert.type === 'course' && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5" />
                                    {formatTimeSpent(cert.time_spent_seconds)}
                                  </span>
                                )}
                                {cert.type === 'learning_path' && cert.completed_courses !== undefined && (
                                  <span className="flex items-center gap-1">
                                    <BookOpen className="w-3.5 h-3.5" />
                                    {cert.completed_courses}/{cert.total_courses} courses
                                  </span>
                                )}
                              </div>

                              {/* Action Buttons */}
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (canWrite) handleDownloadCertificate(cert.pdf_url, cert.title, cert.certificate_number);
                                  }}
                                  disabled={!canWrite}
                                  title={!canWrite ? 'You do not have permission to download' : undefined}
                                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg transition-colors ${
                                    !canWrite ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'
                                  }`}
                                >
                                  <Download className="w-4 h-4" />
                                  Download
                                </button>
                                {allowRegeneration && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openRegenerateConfirm(cert._id, cert.type as CertificateType);
                                    }}
                                    className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                    title="Regenerate Certificate"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCertificateShare(cert);
                                  }}
                                  className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  <Share2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Badges Tab */}
              {activeTab === 'badges' && (
                <div className="space-y-8">
                  {/* Earned Badges */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-gray-900">Earned Badges ({filteredEarnedBadges.length})</h3>
                    </div>
                    {filteredEarnedBadges.length === 0 ? (
                      searchTerm ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                          <Search className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No badges found matching "{searchTerm}"</p>
                        </div>
                      ) : (
                        <EmptyState
                          icon={BadgeIcon}
                          title="Start Your Badge Collection"
                          description="Earn XP by completing lessons, quizzes, and courses. As you learn, you'll unlock badges that showcase your progress and dedication."
                          actionText="Begin Your Journey"
                        />
                      )
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2.5 sm:gap-4">
                        {filteredEarnedBadges.map((badge) => (
                          <div
                            key={badge.id}
                            onClick={() => handleBadgeClick(badge)}
                            /* Phone: one-column list row (icon left, details right).
                               sm and up: the original stacked, centred card. */
                            className="group bg-white border border-gray-200 rounded-xl p-3 sm:p-4 hover:shadow-lg transition-all duration-300 cursor-pointer flex items-center gap-3 text-left sm:block sm:text-center"
                          >
                            <div
                              className={`w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 sm:mx-auto sm:mb-3 rounded-full ${!badge.badgeUrl ? (isInlineColor(badge.iconColor) ? '' : badge.color) : ''} flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300 shadow-lg overflow-hidden`}
                              style={!badge.badgeUrl && isInlineColor(badge.iconColor) ? { backgroundColor: badge.iconColor } : undefined}
                            >
                              {badge.badgeUrl ? (
                                <img src={badge.badgeUrl} alt={badge.name} className="w-full h-full object-cover" />
                              ) : (
                                badge.icon
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-sm text-gray-900 mb-0.5 sm:mb-1 line-clamp-1">{badge.name}</h3>
                              <p className="text-xs text-gray-600 mb-1.5 sm:mb-2 line-clamp-2">{badge.description}</p>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:block">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getRarityBadgeColor(badge.rarity)}`}>
                                  {badge.rarity}
                                </span>
                                <p className="text-xs text-gray-500 sm:mt-2">{badge.points} pts</p>
                                {badge.earnedDate && (
                                  <p className="text-xs text-gray-400 sm:mt-1">
                                    <span className="sm:hidden">· </span>
                                    {new Date(badge.earnedDate).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Locked Badges */}
                  {filteredLockedBadges.length > 0 && (
                    <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Lock className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Locked Badges</h2>
                      <p className="text-sm text-gray-600">Keep learning to unlock these</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2.5 sm:gap-4">
                    {filteredLockedBadges.map((badge) => (
                      <div
                        key={badge.id}
                        /* Phone: one-column list row, same as earned badges. */
                        className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-3 sm:p-5 opacity-60 hover:opacity-80 transition-opacity flex items-center gap-3 text-left sm:block sm:text-center"
                      >
                        <div className="w-14 h-14 sm:w-20 sm:h-20 flex-shrink-0 sm:mx-auto sm:mb-4 rounded-full bg-gradient-to-r from-gray-400 to-gray-500 flex items-center justify-center text-white relative overflow-hidden">
                          {badge.badgeUrl ? (
                            <img src={badge.badgeUrl} alt={badge.name} className="w-full h-full object-cover grayscale" />
                          ) : (
                            badge.icon
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                            <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sm text-gray-900 mb-0.5 sm:mb-2 line-clamp-1 sm:line-clamp-none">{badge.name}</h3>
                          <p className="text-xs text-gray-600 mb-1.5 sm:mb-3 line-clamp-2">{badge.description}</p>
                          <div className="flex items-center sm:justify-center gap-2 mb-1.5 sm:mb-3">
                            <span className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-bold ${getRarityBadgeColor(badge.rarity)}`}>
                              {badge.rarity}
                            </span>
                            <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-bold">
                              {badge.points} pts
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 font-medium">{badge.requirement}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Badge Detail Modal */}
      {showBadgeDetail && selectedBadge && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowBadgeDetail(false);
            setSelectedBadge(null);
          }}
        >
          <div
            className={`relative rounded-3xl shadow-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto ${
              selectedBadge.rarity === 'legendary' ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-100' :
              selectedBadge.rarity === 'epic' ? 'bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100' :
              selectedBadge.rarity === 'rare' ? 'bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100' :
              'bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => {
                setShowBadgeDetail(false);
                setSelectedBadge(null);
              }}
              className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-500 hover:text-gray-700 hover:bg-white transition-all z-20 shadow-sm"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header with badge icon */}
            <div className={`relative px-6 pt-12 pb-16 overflow-hidden ${
              selectedBadge.rarity === 'legendary' ? 'bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500' :
              selectedBadge.rarity === 'epic' ? 'bg-gradient-to-br from-purple-500 via-violet-500 to-purple-600' :
              selectedBadge.rarity === 'rare' ? 'bg-gradient-to-br from-blue-400 via-cyan-400 to-blue-500' :
              'bg-gradient-to-br from-slate-200 via-gray-100 to-slate-200'
            }`}>
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px),
                                    radial-gradient(circle at 80% 50%, white 1px, transparent 1px),
                                    radial-gradient(circle at 40% 20%, white 1px, transparent 1px),
                                    radial-gradient(circle at 60% 80%, white 1px, transparent 1px)`,
                  backgroundSize: '80px 80px'
                }} />
              </div>

              {/* Badge icon container */}
              <div className="relative flex justify-center">
                <div className={`relative w-28 h-28 rounded-2xl border-4 flex items-center justify-center shadow-xl overflow-hidden ${
                  selectedBadge.rarity === 'legendary' ? 'bg-gradient-to-br from-amber-100 to-yellow-100 border-amber-400' :
                  selectedBadge.rarity === 'epic' ? 'bg-gradient-to-br from-purple-100 to-violet-100 border-purple-400' :
                  selectedBadge.rarity === 'rare' ? 'bg-gradient-to-br from-blue-100 to-cyan-100 border-blue-400' :
                  'bg-gradient-to-br from-slate-100 to-slate-200 border-slate-300'
                }`}>
                  {selectedBadge.badgeUrl ? (
                    <img src={selectedBadge.badgeUrl} alt={selectedBadge.name} className="w-20 h-20 object-contain" />
                  ) : (
                    <div style={{ color: selectedBadge.iconColor || '#6b7280' }}>
                      {selectedBadge.icon}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6 text-center -mt-6 relative z-10">
              {/* Rarity badge */}
              <div className="flex justify-center mb-3">
                <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white shadow-md border ${
                  selectedBadge.rarity === 'legendary' ? 'text-amber-600 border-amber-400' :
                  selectedBadge.rarity === 'epic' ? 'text-purple-600 border-purple-400' :
                  selectedBadge.rarity === 'rare' ? 'text-blue-600 border-blue-400' :
                  'text-slate-600 border-slate-300'
                }`}>
                  {selectedBadge.rarity === 'legendary' && <Sparkles className="w-3 h-3" />}
                  {selectedBadge.rarity}
                  {selectedBadge.rarity === 'legendary' && <Sparkles className="w-3 h-3" />}
                </span>
              </div>

              {/* Badge name */}
              <h2 className="text-xl font-bold text-gray-900 leading-tight mb-2 break-words">{selectedBadge.name}</h2>

              {/* Description */}
              <p className="text-gray-600 text-sm mb-5 leading-relaxed">{selectedBadge.description}</p>

              {/* Badge stats — two equal labelled tiles (same as BadgePopup), so
                  a long category can't squeeze the XP value onto two lines. */}
              <div className={`grid ${selectedBadge.category ? 'grid-cols-2' : 'grid-cols-1 max-w-[160px] mx-auto'} gap-2 mb-5`}>
                <div className="flex flex-col items-center justify-center px-2 py-2.5 bg-amber-50 rounded-xl border border-amber-200">
                  <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                    <Zap className="w-3 h-3" />
                    XP
                  </span>
                  <span className="text-base font-bold text-amber-700 whitespace-nowrap">
                    {(selectedBadge.points ?? 0).toLocaleString()}
                  </span>
                </div>
                {selectedBadge.category && (
                  <div className="flex flex-col items-center justify-center px-2 py-2.5 bg-blue-50 rounded-xl border border-blue-200 min-w-0">
                    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                      <Target className="w-3 h-3" />
                      Category
                    </span>
                    <span
                      className="text-sm font-semibold text-blue-700 leading-snug line-clamp-2 break-words max-w-full"
                      title={selectedBadge.category}
                    >
                      {selectedBadge.category}
                    </span>
                  </div>
                )}
              </div>

              {/* Earned Date */}
              {selectedBadge.earnedDate && (
                <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-5">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Earned on {new Date(selectedBadge.earnedDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Certificate Detail Modal */}
      {showCertificateDetail && selectedCertificate && (() => {
        const cert = selectedCertificate;
        const tierConfig = cert.tier ? getTierConfig(cert.tier) : null;
        const typeLabel = cert.type === 'course' ? 'Course Certificate' : cert.type === 'learning_path' ? 'Learning Path Certificate' : 'Certification';
        const typeGradient = cert.type === 'course' ? 'from-emerald-500 via-teal-500 to-cyan-600' : cert.type === 'learning_path' ? 'from-violet-500 via-purple-500 to-indigo-600' : tierConfig?.gradient || 'from-indigo-500 to-purple-600';
        const typeIcon = cert.type === 'course' ? <BookOpen className="w-5 h-5" /> : cert.type === 'learning_path' ? <Layers className="w-5 h-5" /> : <Award className="w-5 h-5" />;

        return (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowCertificateDetail(false);
              setSelectedCertificate(null);
            }}
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <style>{`
                .cert-modal-scroll::-webkit-scrollbar {
                  width: 5px;
                }
                .cert-modal-scroll::-webkit-scrollbar-track {
                  background: transparent;
                }
                .cert-modal-scroll::-webkit-scrollbar-thumb {
                  background: rgba(156, 163, 175, 0.5);
                  border-radius: 10px;
                }
                .cert-modal-scroll::-webkit-scrollbar-thumb:hover {
                  background: rgba(107, 114, 128, 0.7);
                }
              `}</style>
              <div
                className="max-h-[90vh] overflow-y-auto cert-modal-scroll"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
                }}
              >
              {/* Header with Banner */}
              <div className={`relative h-48 bg-gradient-to-br ${typeGradient} overflow-hidden rounded-t-2xl`}>
                {cert.banner_image_url ? (
                  <>
                    <img
                      src={cert.banner_image_url}
                      alt={cert.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Award className="w-24 h-24 text-white/20" />
                  </div>
                )}

                {/* Close button */}
                <button
                  onClick={() => {
                    setShowCertificateDetail(false);
                    setSelectedCertificate(null);
                  }}
                  className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Type Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-white/95 shadow-lg text-gray-700">
                    {typeIcon}
                    {typeLabel}
                  </span>
                </div>

                {/* Certificate Info at bottom */}
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-white/80" />
                    <span className="text-xs font-mono text-white/90">{cert.certificate_number}</span>
                  </div>
                  <h2 className="text-xl font-bold line-clamp-2 drop-shadow-lg">
                    {cert.title}
                  </h2>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-5">
                {/* Key Info Row */}
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {/* Status */}
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    cert.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {cert.status === 'ACTIVE' ? <CheckCircle className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    {cert.status}
                  </span>
                  {/* Tier */}
                  {tierConfig && (
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${tierConfig.bgLight} ${tierConfig.text}`}>
                      <span>{tierConfig.icon}</span>
                      {cert.tier}
                    </span>
                  )}
                  {/* Score */}
                  {cert.percentage_achieved !== undefined && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                      <TrendingUp className="w-3.5 h-3.5" />
                      {cert.percentage_achieved.toFixed(0)}%
                    </span>
                  )}
                  {/* Points */}
                  {cert.type === 'certification' && cert.score_achieved !== undefined && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                      <Target className="w-3.5 h-3.5" />
                      {cert.score_achieved} pts
                    </span>
                  )}
                  {/* Time Spent */}
                  {cert.type === 'course' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">
                      <Clock className="w-3.5 h-3.5" />
                      {formatTimeSpent(cert.time_spent_seconds)}
                    </span>
                  )}
                  {/* Courses */}
                  {cert.type === 'learning_path' && cert.completed_courses !== undefined && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                      <BookOpen className="w-3.5 h-3.5" />
                      {cert.completed_courses}/{cert.total_courses} courses
                    </span>
                  )}
                  {/* Issued Date */}
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(cert.issued_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>

                {/* Description */}
                {cert.description && (
                  <p className="text-sm text-gray-600 leading-relaxed">{cert.description}</p>
                )}

                {/* Credential Info */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 min-w-0">
                  <Users className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium text-gray-700">{cert.holder_info.user_name}</span>
                  <span className="hidden sm:inline text-gray-300">|</span>
                  <span className="truncate max-w-full">{cert.holder_info.user_email}</span>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => canWrite && handleDownloadCertificate(cert.pdf_url, cert.title, cert.certificate_number)}
                    disabled={!canWrite}
                    title={!canWrite ? 'You do not have permission to download' : undefined}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg transition-colors text-sm font-semibold ${
                      !canWrite ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'
                    }`}
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  {allowRegeneration && (
                    <button
                      onClick={() => {
                        setShowCertificateDetail(false);
                        setSelectedCertificate(null);
                        openRegenerateConfirm(cert._id, cert.type as CertificateType);
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg transition-colors text-sm font-semibold hover:bg-gray-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Regenerate
                    </button>
                  )}
                </div>

                {/* Share */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 text-center">Share</p>
                  <SocialShare
                    content={{
                      title: cert.title,
                      description: cert.tier
                        ? `${cert.tier} Tier | Score: ${cert.percentage_achieved?.toFixed(0) || 0}%`
                        : cert.type === 'learning_path'
                          ? `Completed ${cert.completed_courses || 0}/${cert.total_courses || 0} courses`
                          : undefined,
                      url: getVerificationUrl(cert.verification_url, cert.certificate_number),
                      hashtags: ['Skillzen', 'Certificate', 'Achievement', 'Learning'],
                      certificateId: cert.certificate_number,
                      organizationId: linkedinOrgId,
                      issueDate: cert.issued_date,
                    }}
                    variant="compact"
                    showAddToLinkedIn={true}
                  />
                </div>
              </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showCertificateShare && shareCertificate && (() => {
        const cert = shareCertificate;
        const tierConfig = cert.tier ? getTierConfig(cert.tier) : null;
        const typeLabel = cert.type === 'course' ? 'Course Certificate' : cert.type === 'learning_path' ? 'Learning Path Certificate' : 'Certification';

        return (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowCertificateShare(false);
              setShareCertificate(null);
            }}
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5 text-white">
                <button
                  onClick={() => {
                    setShowCertificateShare(false);
                    setShareCertificate(null);
                  }}
                  className="absolute top-4 right-4 p-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Share2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Share Your Achievement</h3>
                    <p className="text-white/80 text-sm">Let the world know about your success!</p>
                  </div>
                </div>
              </div>

              <div className="p-5 border-b border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2 line-clamp-2">{cert.title}</h4>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full">
                    <Award className="w-3 h-3" />
                    {typeLabel}
                  </span>
                  {cert.tier && tierConfig && (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${tierConfig.bgLight} ${tierConfig.text}`}>
                      <span>{tierConfig.icon}</span>
                      {cert.tier}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(cert.issued_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <SocialShare
                  content={{
                    title: cert.title,
                    description: cert.tier
                      ? `${cert.tier} Tier | Score: ${cert.percentage_achieved?.toFixed(0) || 0}%`
                      : cert.type === 'learning_path'
                        ? `Completed ${cert.completed_courses || 0}/${cert.total_courses || 0} courses`
                        : undefined,
                    url: getVerificationUrl(cert.verification_url, cert.certificate_number),
                    hashtags: ['Skillzen', 'Certificate', 'Achievement', 'Learning'],
                    certificateId: cert.certificate_number,
                    organizationId: linkedinOrgId,
                    issueDate: cert.issued_date,
                  }}
                  variant="buttons"
                  showAddToLinkedIn={true}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Regenerate Certificate Confirmation Modal */}
      {showRegenerateConfirm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !regenerating && setShowRegenerateConfirm(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-indigo-100 flex items-center justify-center">
                <RefreshCw className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Regenerate Certificate?</h3>
              <p className="text-sm text-gray-600 mb-6">
                This will regenerate your certificate with the latest information. The existing certificate will be replaced.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRegenerateConfirm(false)}
                  disabled={regenerating}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegenerateCertificate}
                  disabled={regenerating}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {regenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Regenerate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}
